import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { currencyFormatter } from "@common/helpers/currency";
import { fromIntToMonetary, fromMonetaryToInt } from "@common/helpers/monetary";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useAccrualLedgerStatus } from "@api/ledger.api";
import { useAllowFeature } from "@hooks/useAllowFeature";
import {
    SpreadsheetGrid,
    ChangedCells,
} from "@components/spreadsheet/SpreadsheetGrid";
import { ColumnDef, SpreadsheetData } from "@components/spreadsheet/types";
import {
    BaseCellProps,
    CategoryCell,
    CellType,
} from "@components/spreadsheet/cells";
import { withRemoveLineColumn } from "@components/spreadsheet/removeLineColumn";
import { Button } from "@design-system/Button/Button";
import { PlusIcon } from "@icons";
import { TransactionSplitClassCell } from "@components/transactions/TransactionSplitV2/TransactionSplitClassCell";
import { createLedgerColumn } from "@components/transactions/TransactionSplitV2/createLedgerColumn";
import { TransactionSplitPercentCell } from "@components/transactions/TransactionSplitV2/TransactionSplitPercentCell";
import {
    addCheckSplit,
    amountToPercent,
    applyCheckLineAmountEdit,
    applyCheckLinePercentEdit,
    checkLineAmounts,
    emptyCheckLine,
    PARENT_CHECK_LINE_ID,
    RecordCheckFormValues,
    RecordCheckLine,
} from "./recordCheckForm";
import styles from "./RecordCheckModal.module.scss";

interface Props {
    entityId?: number;
    isGlFirst: boolean;
}

const toStringOrNull = (value: BaseCellProps["value"]) =>
    typeof value === "string" ? value : null;

const gridTemplateColumns = ({
    hasClasses,
    showCategory,
    showAccrualLedger,
}: {
    hasClasses: boolean;
    showCategory: boolean;
    showAccrualLedger: boolean;
}) => {
    const parts = ["40px", "1fr"];
    if (showCategory) {
        parts.push("1fr");
    }
    parts.push("1fr");
    if (showAccrualLedger) {
        parts.push("1fr");
    }
    if (hasClasses) {
        parts.push("200px");
    }
    parts.push("160px", "80px");
    return parts.join(" ");
};

const isRemovedRow = (row?: SpreadsheetData[number]) =>
    row === undefined ||
    (row.description == null &&
        row.categoryId == null &&
        row.accountId == null &&
        row.accrualAccountId == null &&
        row.amount === undefined &&
        (!Array.isArray(row.classIds) || row.classIds.length === 0));

const toLine = (row: SpreadsheetData[number], previous: RecordCheckLine) => ({
    description: toStringOrNull(row.description) ?? "",
    accountId: toStringOrNull(row.accountId) ?? "",
    accrualAccountId: toStringOrNull(row.accrualAccountId) ?? "",
    categoryId: toStringOrNull(row.categoryId) ?? "",
    classIds: Array.isArray(row.classIds)
        ? row.classIds.filter((id): id is string => typeof id === "string")
        : [],
    amount: typeof row.amount === "number" ? row.amount : previous.amount,
});

export const RecordCheckLines = ({ entityId, isGlFirst }: Props) => {
    const formik = useFormikContext<RecordCheckFormValues>();
    const { hasAccess: hasClassesAccess } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );
    const { values, setFieldValue } = formik;
    const showCategory = !isGlFirst;
    const {
        hasAccrualLedger,
        isLoading: isLoadingLedgers,
        isError: isLedgersError,
    } = useAccrualLedgerStatus(entityId);
    const showAccrualLedger =
        entityId !== undefined &&
        !isLoadingLedgers &&
        !isLedgersError &&
        hasAccrualLedger;
    const checkAmount = values.amount ?? 0;

    const data: SpreadsheetData = useMemo(() => {
        const lineAmounts = checkLineAmounts(values.lines, checkAmount);
        return values.lines.map((line, index) => ({
            _id: index === 0 ? PARENT_CHECK_LINE_ID : undefined,
            _isParent: index === 0,
            description: line.description || null,
            categoryId: line.categoryId || null,
            accountId: line.accountId || null,
            accrualAccountId: line.accrualAccountId || null,
            classIds: line.classIds,
            amount: lineAmounts[index] ?? 0,
            percentage: amountToPercent(lineAmounts[index] ?? 0, checkAmount),
        }));
    }, [values.lines, checkAmount]);

    const columns: ColumnDef[] = useMemo(() => {
        const createCheckLedgerColumn = (ledgerBasis: AccountingLedgerBasis) =>
            createLedgerColumn({
                ledgerBasis,
                getEntityId: () => entityId,
                isEditable: entityId !== undefined,
                canClear:
                    showCategory ||
                    ledgerBasis === AccountingLedgerBasis.accruals,
            });
        const ledgerColumns = [
            createCheckLedgerColumn(AccountingLedgerBasis.cash),
            ...(showAccrualLedger
                ? [createCheckLedgerColumn(AccountingLedgerBasis.accruals)]
                : []),
        ];

        return [
            { field: "", label: "", cellType: CellType.ROW_NUMBER },
            {
                field: "description",
                label: "Description",
                cellType: CellType.STRING,
                placeholder: "Add description…",
            },
            ...(showCategory
                ? [
                      {
                          field: "categoryId",
                          label: "Category",
                          cellType: CellType.CATEGORY,
                          cellFn: (props: BaseCellProps) => (
                              <CategoryCell
                                  {...props}
                                  value={toStringOrNull(props.value)}
                              />
                          ),
                      },
                  ]
                : []),
            ...ledgerColumns,
            ...(hasClassesAccess
                ? [
                      {
                          field: "classIds",
                          label: "Class",
                          cellType: CellType.CLASS,
                          cellFn: (props: BaseCellProps) => (
                              <TransactionSplitClassCell
                                  {...props}
                                  value={
                                      Array.isArray(props.value)
                                          ? props.value
                                          : null
                                  }
                              />
                          ),
                      },
                  ]
                : []),
            {
                field: "amount",
                label: "Amount",
                cellType: CellType.MATH,
                isMonetary: true,
                aggregation: (rows: SpreadsheetData) => {
                    const total = rows.reduce(
                        (sum, row) =>
                            sum +
                            fromMonetaryToInt(
                                typeof row.amount === "number" ? row.amount : 0,
                            ),
                        0,
                    );
                    return currencyFormatter.format(fromIntToMonetary(total));
                },
            },
            {
                field: "percentage",
                label: <span className={styles.percentHeader}>%</span>,
                cellType: CellType.MATH,
                cellFn: (props) => (
                    <TransactionSplitPercentCell
                        {...props}
                        value={
                            typeof props.value === "number" ? props.value : null
                        }
                    />
                ),
            },
        ];
    }, [showCategory, showAccrualLedger, entityId, hasClassesAccess]);

    const isCellEditable = useCallback(
        (rowIdx: number, colIdx: number): boolean => {
            if (colIdx === 0) {
                return rowIdx > 0;
            }
            const columnField = columns[colIdx]?.field;
            if (
                entityId === undefined &&
                (columnField === "accountId" ||
                    columnField === "accrualAccountId")
            ) {
                return false;
            }
            if (
                rowIdx === 0 &&
                (columnField === "amount" || columnField === "percentage")
            ) {
                return false;
            }
            return true;
        },
        [columns, entityId],
    );

    const handleChange = useCallback(
        (newData: SpreadsheetData, changedCells: ChangedCells) => {
            let nextLines: RecordCheckLine[] = newData.map((row, index) =>
                toLine(
                    row,
                    values.lines[index] ?? values.lines[0] ?? emptyCheckLine(),
                ),
            );

            for (const [rowIdx, colIdx] of changedCells) {
                const field = columns[colIdx]?.field;
                if (rowIdx === 0) {
                    continue;
                }
                if (field === "percentage") {
                    const percent =
                        typeof newData[rowIdx]?.percentage === "number"
                            ? newData[rowIdx].percentage
                            : 0;
                    nextLines = applyCheckLinePercentEdit({
                        lines: nextLines,
                        rowIndex: rowIdx,
                        percent,
                        checkAmount,
                    });
                } else if (field === "amount") {
                    const amount =
                        typeof newData[rowIdx]?.amount === "number"
                            ? newData[rowIdx].amount
                            : 0;
                    nextLines = applyCheckLineAmountEdit({
                        lines: nextLines,
                        rowIndex: rowIdx,
                        amount,
                        checkAmount,
                    });
                }
            }

            nextLines = nextLines.filter(
                (_line, index) => index === 0 || !isRemovedRow(newData[index]),
            );

            void setFieldValue("lines", nextLines);
        },
        [checkAmount, columns, setFieldValue, values.lines],
    );

    const grid = useMemo(
        () =>
            withRemoveLineColumn({
                columns,
                gridTemplateColumns: gridTemplateColumns({
                    hasClasses: hasClassesAccess,
                    showCategory,
                    showAccrualLedger,
                }),
            }),
        [columns, hasClassesAccess, showCategory, showAccrualLedger],
    );

    const handleAddSplit = useCallback(() => {
        if (checkAmount <= 0) {
            return;
        }
        void setFieldValue("lines", addCheckSplit(values.lines, checkAmount));
    }, [checkAmount, setFieldValue, values.lines]);

    return (
        <div className={styles.lines}>
            <SpreadsheetGrid
                data={data}
                columns={grid.columns}
                onChange={handleChange}
                isCellEditable={isCellEditable}
                gridTemplateColumns={grid.gridTemplateColumns}
                showTotalsRow
                lightNotEditableCells
                showDropdownIndicator
            />
            <div className={styles.addSplit}>
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    disabled={checkAmount <= 0}
                    onClick={handleAddSplit}
                    leftIcon={<PlusIcon />}
                >
                    Add split
                </Button>
            </div>
        </div>
    );
};
